/* eslint-disable @typescript-eslint/no-empty-function */
import styled from 'styled-components'
import { DashboardCard, Flex, P } from 'styling/GlobalStyles'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IWidget } from 'types/dashboard.interface'
import { TitleText } from 'styling/Typography'
import { WidgetEditOverlay } from 'pages/Dashboard/Dashboard.styled'
import { ComingSoonOverlay } from '../HrDashboard.styled'

type MealStatus = 'done' | 'active' | 'upcoming'

type Meal = {
  name: string
  status: MealStatus
  served: number
  total: number
}

const MEALS: Meal[] = [
  { name: 'Breakfast', status: 'done', served: 172, total: 180 },
  { name: 'Lunch', status: 'active', served: 0, total: 195 },
  { name: 'Dinner', status: 'upcoming', served: 0, total: 190 },
]

const STATUS_COLORS: Record<MealStatus, string> = {
  done: '#22c55e',
  active: '#ff9900',
  upcoming: '#9ca3af',
}

const STATUS_LABELS: Record<MealStatus, string> = {
  done: '✓ Done',
  active: 'Active',
  upcoming: 'Upcoming',
}

/* ── Styled components ── */

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 8px;
`

const WindowBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colorOrange};
  white-space: nowrap;
`

const GreenDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  gap: 0.5rem;
`

const MealRow = styled.div<{ $active: boolean }>`
  border-radius: 10px;
  padding: 10px 14px;
  border: 1.5px solid
    ${(p) => (p.$active ? '#ff9900' : 'transparent')};
  background: ${(p) =>
    p.$active ? 'rgba(255,153,0,0.08)' : 'transparent'};
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const MealTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const MealLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MealName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textColor};
`

const StatusBadge = styled.span<{ $color: string }>`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${(p) => p.$color};
`

const MealCount = styled.span<{ $color: string }>`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(p) => p.$color};
`

const TrackWrap = styled.div`
  position: relative;
  width: 100%;
  height: 3px;
  border-radius: 2px;
  overflow: hidden;
`

const Track = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.textColor};
  opacity: 0.1;
  border-radius: 2px;
`

const Fill = styled.div<{ $color: string; $pct: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${(p) => p.$pct}%;
  max-width: 100%;
  background: ${(p) => p.$color};
  border-radius: 2px;
`

const Footer = styled.div`
  width: 100%;
  text-align: right;
  padding-top: 4px;
`

const MealsTotal = styled.span`
  font-size: 1.1rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colorOrange};
`

/* ── Component ── */

export const HrFoodOperationsWidget = ({
  modalOpen,
  removeWidget,
  onDragOver,
  onDragStart,
  onDragDrop,
}: IWidget) => {
  const activeMeal = MEALS.find((m) => m.status === 'active')
  const pendingMeals = 15

  return (
    <DashboardCard
      draggable={modalOpen}
      onDragStartCapture={() => onDragStart('food', 1)}
      onDrop={onDragDrop}
      onDragEnd={(e) => e.preventDefault()}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver('food')
      }}
      width='1fr'
      style={{ gridArea: 'food', cursor: modalOpen ? 'grab' : 'normal' }}
    >
      {modalOpen && (
        <WidgetEditOverlay
          onClick={(e) => {
            removeWidget('food')
            e.stopPropagation()
          }}
        >
          <P>Remove Widget</P>
          <CloseIcon />
        </WidgetEditOverlay>
      )}
      <ComingSoonOverlay>Coming Soon</ComingSoonOverlay>

      <CardHeader>
        <TitleText weight='bold' style={{ margin: 0 }}>
          FOOD OPERATIONS
        </TitleText>
        {activeMeal && (
          <WindowBadge>
            <GreenDot />
            {activeMeal.name} window open
          </WindowBadge>
        )}
      </CardHeader>

      <Content>
        {MEALS.map((meal) => {
          const color = STATUS_COLORS[meal.status]
          const pct = meal.total > 0 ? Math.round((meal.served / meal.total) * 100) : 0
          return (
            <MealRow key={meal.name} $active={meal.status === 'active'}>
              <MealTop>
                <MealLeft>
                  <MealName>{meal.name}</MealName>
                  <StatusBadge $color={color}>{STATUS_LABELS[meal.status]}</StatusBadge>
                </MealLeft>
                <MealCount $color={color}>
                  {meal.served}/{meal.total}
                </MealCount>
              </MealTop>
              <TrackWrap>
                <Track />
                <Fill $color={color} $pct={pct} />
              </TrackWrap>
            </MealRow>
          )
        })}
      </Content>

      <Footer>
        <Flex between center width='100%'>
          <span style={{ fontSize: '0.75rem', color: 'var(--grey-text, #9ca3af)' }}>
            Pending special meals
          </span>
          <MealsTotal>{pendingMeals} meals</MealsTotal>
        </Flex>
      </Footer>
    </DashboardCard>
  )
}
